"use client";

import React, { useRef, useState } from "react";
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from "react-zoom-pan-pinch";
import Image from "next/image";
import Link from "next/link";
import { format } from "date-fns";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverPortal,
} from "@radix-ui/react-popover";
import {
  Plus,
  Minus,
  RotateCcw,
  X,
  ArrowRight,
  Calendar,
  Compass,
  Trophy,
} from "lucide-react";
import filteredPoints from "../public/static/data/map.json";
import { REGION_COLORS, Region } from "@/lib/regions";
import { formatTierRank } from "@/lib/ranks";
import { buildRankUri } from "@/lib/imagecdn";
import { cn } from "@/lib/utils";
import { RegionBadge } from "./lol/RegionBadge";
import { MapClickOverlay } from "./RussiaPolygon";

export interface ServerMapRegionAccountMap {
  [x: string]: ServerMapAccountState;
}

export interface ServerMapAccountProps {
  accounts?: ServerMapRegionAccountMap;
}

export interface ServerMapAccountState {
  accountName: string;
  region: string;
  rank: number;
  tier: string;
  lp: number;
  wins: number;
  losses: number;
  timestamp: Date | string;
  isIngame?: boolean;
  accountTag?: string;
  summonerId?: string;
}

/**
 * Coordinates and metadata for all regional League server hubs on the 200x90 map
 */
export const SERVER_HUBS: Record<
  string,
  { x: number; y: number; name: string }
> = {
  EUW: { x: 99, y: 23.4, name: "Europe West" },
  EUNE: { x: 106, y: 21.7, name: "Europe Nordic & East" },
  NA: { x: 48.5, y: 29.4, name: "North America" },
  KR: { x: 161.5, y: 32.9, name: "Korea" },
  BR: { x: 70, y: 66.7, name: "Brazil" },
  LAN: { x: 42.5, y: 43.3, name: "Latin America North" },
  LAS: { x: 57.5, y: 72.8, name: "Latin America South" },
  OCE: { x: 174.5, y: 72.8, name: "Oceania" },
  JP: { x: 168, y: 33.8, name: "Japan" },
  TR: { x: 110, y: 30.3, name: "Turkey" },
  RU: { x: 114.5, y: 19.1, name: "Russia" },
  CHN: { x: 158.5, y: 36.4, name: "China" },
  TW2: { x: 157.5, y: 39.8, name: "Taiwan" },
  ME: { x: 119.5, y: 40.5, name: "Middle East" },
};

/**
 * Key historical expedition trajectories between hubs
 */
const EXPEDITION_ROUTES: [string, string][] = [
  ["EUW", "NA"],
  ["EUW", "KR"],
  ["NA", "BR"],
  ["EUW", "TR"],
  ["EUW", "EUNE"],
  ["EUW", "ME"],
  ["TR", "ME"],
  ["NA", "LAN"],
  ["LAN", "LAS"],
  ["KR", "JP"],
  ["KR", "CHN"],
  ["CHN", "TW2"],
  ["KR", "OCE"],
];

function createArc(
  start: { x: number; y: number },
  end: { x: number; y: number },
  bend = 0.22
) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dist = Math.hypot(dx, dy);
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  const cx = midX;
  const cy = Math.max(3, midY - dist * bend);
  return `M ${start.x} ${start.y} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${end.x} ${end.y}`;
}

/**
 * Corner HUD zoom controls (+, -, Reset)
 */
const MapControls = () => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 bg-[#09130e]/90 backdrop-blur-md border border-[#172b22] p-1.5 rounded-xl shadow-2xl">
      <button
        onClick={() => zoomIn(0.25)}
        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/15 text-white/80 hover:text-white transition-all text-sm font-semibold border border-white/5 active:scale-95"
        title="Vergrößern (+)"
        type="button"
      >
        <Plus className="w-4 h-4" />
      </button>
      <button
        onClick={() => zoomOut(0.25)}
        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/15 text-white/80 hover:text-white transition-all text-sm font-semibold border border-white/5 active:scale-95"
        title="Verkleinern (-)"
        type="button"
      >
        <Minus className="w-4 h-4" />
      </button>
      <button
        onClick={() => resetTransform()}
        className="px-2.5 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/15 text-white/80 hover:text-white transition-all text-xs font-semibold border border-white/5 gap-1.5 active:scale-95"
        title="Kartenansicht zurücksetzen"
        type="button"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        <span className="font-mono text-[11px] font-bold">RESET</span>
      </button>
    </div>
  );
};

/**
 * Holographic Glass Popover Card for a conquered Server / Account
 */
export const HolographicServerCard = ({
  account,
  onClose,
}: {
  account: ServerMapAccountState;
  onClose: () => void;
}) => {
  let formattedDate = "N/A";
  try {
    const d = new Date(account.timestamp);
    if (!isNaN(d.getTime())) {
      formattedDate = format(d, "dd.MM.yyyy");
    }
  } catch {
    formattedDate = "N/A";
  }

  const tierUpper = (account.tier || "CHALLENGER").toUpperCase();
  const tierLower = tierUpper.toLowerCase();
  const rankUrl = buildRankUri(tierUpper, "full");
  const totalGames = (account.wins || 0) + (account.losses || 0);
  const winRate =
    totalGames > 0
      ? Math.round(((account.wins || 0) / totalGames) * 100)
      : 100;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#09130e]/95 backdrop-blur-2xl border border-[#172b22] p-5 shadow-[0_0_50px_rgba(0,0,0,0.9),0_0_25px_rgba(16,185,129,0.25)] text-white">
      {/* Top radiant glow accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-500 via-noway-default to-amber-400" />

      {/* Header: Region Badge, Title, Close Button */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <RegionBadge region={account.region} longName />
          <span className="text-[11px] font-mono text-white/50 uppercase tracking-wider">
            Sektor {account.region}
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
          type="button"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Account Identity */}
      <div className="mb-4">
        <h3 className="text-xl font-black tracking-tight flex items-baseline gap-1.5">
          <span className="bg-gradient-to-r from-white via-white/95 to-white/75 bg-clip-text text-transparent">
            {account.accountName}
          </span>
          {account.accountTag && (
            <span className="text-sm font-semibold text-noway-default font-mono">
              #{account.accountTag}
            </span>
          )}
        </h3>
      </div>

      {/* 3D Tier Helmet, LP & Peak Date */}
      <div className="flex items-center gap-4 p-3.5 rounded-xl bg-white/[0.04] border border-white/10 mb-4 relative overflow-hidden">
        <div className="relative flex-shrink-0 w-[68px] h-[68px] flex items-center justify-center">
          <Image
            src={rankUrl}
            alt={tierUpper}
            width={68}
            height={68}
            className="drop-shadow-[0_0_16px_rgba(245,158,11,0.6)] object-contain"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.srcset = "";
              target.src = `/static/images/ranked/${tierLower}.png`;
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-wider text-amber-400 font-bold flex items-center gap-1">
            <Trophy className="w-3 h-3 text-amber-400" />
            <span>{formatTierRank(account.tier, account.rank)}</span>
          </div>
          <div className="text-[11px] text-white/50 flex items-center gap-1 mt-0.5 font-mono">
            <Calendar className="w-3 h-3" />
            <span>Bestzeit: {formattedDate}</span>
          </div>
        </div>
      </div>

      {/* Win Rate Gauge & Games Count */}
      <div className="space-y-1.5 mb-5 p-3 rounded-xl bg-white/[0.02] border border-white/5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-white/60 font-medium">Siegquote</span>
          <span
            className={cn(
              "font-bold font-mono",
              winRate >= 60
                ? "text-noway-mint"
                : winRate >= 50
                ? "text-emerald-400"
                : "text-red-400"
            )}
          >
            {winRate}% ({account.wins}W / {account.losses}L)
          </span>
        </div>
        {/* Dual-color gauge */}
        <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-noway-mint to-emerald-400 rounded-l-full"
            style={{ width: `${winRate}%` }}
          />
          <div
            className="h-full bg-red-500/80 rounded-r-full"
            style={{ width: `${100 - winRate}%` }}
          />
        </div>
        <div className="text-[10px] text-white/40 text-right font-mono">
          {totalGames} Spiele gesamt
        </div>
      </div>

      {/* Action CTA Link */}
      <Link
        href={`/matches?account=${encodeURIComponent(account.accountName)}`}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-streamer-purple via-[#7C3AED] to-noway-mint text-white font-semibold text-sm hover:brightness-110 active:scale-[0.98] transition-all shadow-lg shadow-streamer-purple/25"
      >
        <span>Matchverlauf ansehen</span>
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

/**
 * Fallback Card when a region has no account
 */
export const FallBackServerMapAccountCard = ({
  region,
  onClose,
}: {
  region?: Region | null;
  onClose?: () => void;
}) => {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#09130e]/95 backdrop-blur-2xl border border-[#172b22] p-5 shadow-2xl text-white">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-white/10 via-white/30 to-white/10" />
      <div className="flex items-center justify-between mb-4">
        {region ? (
          <RegionBadge region={region} longName />
        ) : (
          <span className="text-sm font-semibold">Unbekannter Sektor</span>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
            type="button"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-center space-y-2">
        <div className="w-10 h-10 mx-auto rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40">
          <Compass className="w-5 h-5 animate-pulse" />
        </div>
        <h4 className="text-base font-bold text-white/90">Unerforschtes Gebiet</h4>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Noway hat auf diesem Server noch keine Challenger-Expedition gestartet.
          Bleib dran für zukünftige World-Tour-Kampagnen!
        </p>
      </div>
    </div>
  );
};

export const ServerMap = (props?: ServerMapAccountProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<ServerMapAccountState | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [anchorPos, setAnchorPos] = useState<{ x: number; y: number } | null>(
    null
  );

  const regions = Object.values(Region ?? {});

  const handleRegionSelect = (
    e: React.SyntheticEvent,
    region: string
  ) => {
    e.stopPropagation();
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const mouseEvent = "clientX" in e ? (e as React.MouseEvent) : undefined;
      const clientX =
        mouseEvent && mouseEvent.clientX > 0
          ? mouseEvent.clientX
          : rect.left + rect.width / 2;
      const clientY =
        mouseEvent && mouseEvent.clientY > 0
          ? mouseEvent.clientY
          : rect.top + rect.height / 2;

      const x = Math.max(30, Math.min(rect.width - 30, clientX - rect.left));
      const y = Math.max(30, Math.min(rect.height - 30, clientY - rect.top));
      setAnchorPos({ x, y });
    }
    const acc = props?.accounts?.[region] ?? null;
    setSelectedAccount(acc);
    setSelectedRegion(region as Region);
    setPopoverOpen(true);
  };

  return (
    <div
      ref={mapContainerRef}
      className="relative w-full rounded-2xl border border-white/10 bg-[#050a08]/90 shadow-2xl backdrop-blur-xl overflow-hidden group h-[440px] sm:h-[500px] md:h-[560px] lg:h-[600px]"
    >
      {/* Bottom-left Legend */}
      <div className="absolute bottom-4 left-4 z-20 hidden sm:flex items-center gap-4 px-3.5 py-1.5 rounded-xl bg-[#09130e]/85 backdrop-blur-md border border-[#172b22] text-[11px] font-medium text-white/60 shadow-lg pointer-events-none">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B] shadow-[0_0_8px_#F59E0B]" />
          <span>Challenger Erobert</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
          <span>Grandmaster</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 border-t border-dashed border-noway-mint/60" />
          <span>Expeditionsroute</span>
        </div>
      </div>

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverAnchor
          className="absolute pointer-events-none"
          style={
            anchorPos
              ? {
                  left: `${anchorPos.x}px`,
                  top: `${anchorPos.y}px`,
                  width: "1px",
                  height: "1px",
                }
              : { left: "50%", top: "50%", width: "1px", height: "1px" }
          }
        />
        <PopoverPortal>
          <PopoverContent
            side="top"
            align="center"
            sideOffset={14}
            className="z-[999] w-96 max-w-[calc(100vw-2rem)] p-0 rounded-2xl border border-white/15 bg-transparent focus:outline-none animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          >
            {selectedAccount ? (
              <HolographicServerCard
                account={selectedAccount}
                onClose={() => setPopoverOpen(false)}
              />
            ) : (
              <FallBackServerMapAccountCard
                region={selectedRegion}
                onClose={() => setPopoverOpen(false)}
              />
            )}
          </PopoverContent>
        </PopoverPortal>

        <TransformWrapper
          centerOnInit={true}
          wheel={{ smoothStep: 0.0002 }}
          maxScale={2.5}
          minScale={0.8}
        >
          <TransformComponent
            contentClass="w-full h-full"
            contentStyle={{ width: "100%", height: "100%" }}
            wrapperStyle={{
              width: "100%",
              height: "100%",
              background: "transparent",
            }}
          >
            <svg
              viewBox="0 0 200 90"
              onClick={() => setPopoverOpen(false)}
              className="w-full h-full select-none"
              style={{ background: "transparent" }}
            >
              <defs>
                {/* Trajectory linear gradient */}
                <linearGradient
                  id="trajectory-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#9146FF" stopOpacity="0.75" />
                  <stop offset="50%" stopColor="#00F5B8" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.75" />
                </linearGradient>

                {/* Beacon glowing filter */}
                <filter
                  id="beacon-glow"
                  x="-50%"
                  y="-50%"
                  width="200%"
                  height="200%"
                >
                  <feGaussianBlur stdDeviation="0.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Base World Continent Dots */}
              {filteredPoints.map((point) => {
                const regionId = point?.data?.id as Region;
                const rankForRegion = regionId
                  ? props?.accounts?.[regionId]
                  : undefined;
                const color = regionId
                  ? rankForRegion
                    ? REGION_COLORS[regionId] ?? "#8FA3B8"
                    : "#334155"
                  : "#1E293B";
                const opacity = rankForRegion ? 0.7 : 0.4;
                const radius = 0.35;

                return (
                  <circle
                    key={`${point.x}-${point.y}`}
                    cx={point.x}
                    cy={point.y}
                    r={radius}
                    fill={color}
                    opacity={opacity}
                  />
                );
              })}

              {/* SVG Arc Trajectories between Conquered Expedition Hubs */}
              {EXPEDITION_ROUTES.map(([from, to]) => {
                const p1 = SERVER_HUBS[from];
                const p2 = SERVER_HUBS[to];
                if (!p1 || !p2) return null;

                return (
                  <path
                    key={`route-${from}-${to}`}
                    d={createArc(p1, p2)}
                    fill="none"
                    stroke="url(#trajectory-gradient)"
                    strokeWidth={0.35}
                    strokeDasharray="1.5 1"
                    strokeLinecap="round"
                    className="opacity-75 pointer-events-none"
                  />
                );
              })}

              {/* Glowing Beacon Pins for Server Hubs */}
              {Object.entries(SERVER_HUBS).map(([region, hub]) => {
                const account = props?.accounts?.[region];
                const tier = account?.tier?.toUpperCase();
                const isChallenger = tier === "CHALLENGER";
                const isGrandmaster = tier === "GRANDMASTER";
                const isMaster = tier === "MASTER";

                const pinColor = isChallenger
                  ? "#F59E0B"
                  : isGrandmaster
                  ? "#EF4444"
                  : isMaster
                  ? "#A855F7"
                  : "#64748B";

                return (
                  <g
                    key={`hub-${region}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select ${region} Server Hub`}
                    onClick={(e) => handleRegionSelect(e, region)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRegionSelect(e, region);
                      }
                    }}
                    className="cursor-pointer group focus:outline-none"
                  >
                    {/* Concentric Radar Pulse Wave Rings for Challenger */}
                    {isChallenger && (
                      <g className="pointer-events-none">
                        <circle
                          cx={hub.x}
                          cy={hub.y}
                          r={2.6}
                          fill="none"
                          stroke="#F59E0B"
                          strokeWidth={0.2}
                          className="animate-ping"
                          style={{
                            transformOrigin: "center",
                            transformBox: "fill-box",
                          }}
                        />
                        <circle
                          cx={hub.x}
                          cy={hub.y}
                          r={1.8}
                          fill="none"
                          stroke="#00F5B8"
                          strokeWidth={0.16}
                          opacity={0.6}
                        />
                      </g>
                    )}

                    {/* Outer Pin Halo / Hover Ring */}
                    <circle
                      cx={hub.x}
                      cy={hub.y}
                      r={isChallenger ? 1.5 : 1.2}
                      fill={pinColor}
                      fillOpacity={0.25}
                      className="group-hover:scale-125 transition-transform"
                      style={{
                        transformOrigin: "center",
                        transformBox: "fill-box",
                      }}
                    />

                    {/* Core Beacon Pin */}
                    <circle
                      cx={hub.x}
                      cy={hub.y}
                      r={isChallenger ? 1.05 : 0.85}
                      fill={pinColor}
                      stroke="#FFFFFF"
                      strokeWidth={0.22}
                      filter="url(#beacon-glow)"
                      className="group-hover:scale-125 transition-transform"
                      style={{
                        transformOrigin: "center",
                        transformBox: "fill-box",
                      }}
                    />

                    {/* Apex Cyan Inner Center for Challenger */}
                    {isChallenger && (
                      <circle
                        cx={hub.x}
                        cy={hub.y}
                        r={0.4}
                        fill="#00F5B8"
                        className="pointer-events-none"
                      />
                    )}

                    {/* Server Region Pill Tag */}
                    <g className="pointer-events-none select-none">
                      <rect
                        x={hub.x - 3.2}
                        y={hub.y + 1.8}
                        width={6.4}
                        height={2.2}
                        rx={0.6}
                        fill="#09130e"
                        fillOpacity={0.92}
                        stroke={isChallenger ? "#F59E0B" : "#172b22"}
                        strokeWidth={0.16}
                      />
                      <text
                        x={hub.x}
                        y={hub.y + 3.4}
                        textAnchor="middle"
                        fontSize={1.3}
                        fontWeight="700"
                        fill={isChallenger ? "#FCD34D" : "#E2E8F0"}
                        letterSpacing="0.04em"
                        style={{ fontFamily: "monospace" }}
                      >
                        {region}
                      </text>
                    </g>
                  </g>
                );
              })}

              {/* Regional Clickable SVG Overlays */}
              {regions.map((region) => {
                return (
                  <MapClickOverlay
                    key={region}
                    onClick={(e) => handleRegionSelect(e, region)}
                    region={region}
                  />
                );
              })}
            </svg>
          </TransformComponent>
          <MapControls />
        </TransformWrapper>
      </Popover>
    </div>
  );
};
